import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Plus, X, Trash2, Flag, ChevronRight, Download } from 'lucide-react';
import { exportTimelinePDF } from '../utils/exportTimelinePDF';

const LEFT_COL_W = 200;
const LANE_H = 40;
const BAR_H = 26;
const MONTH_HDR_H = 22;
const DAY_HDR_H = 26;
const ROW_PAD = 8;

const STATUS = {
  planned:       { bg: '#eff6ff', border: '#93c5fd', text: '#1e40af', label: 'Planned' },
  'in-progress': { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', label: 'In Progress' },
  done:          { bg: '#f0fdf4', border: '#86efac', text: '#166534', label: 'Done' },
};

/* ── date utils ── */
const toLocal = (iso) => {
  if (!iso) return new Date(0);
  const [y, m, d] = iso.split('T')[0].split('-').map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const daysBetween = (a, b) => Math.round((b - a) / 86400000);
const toIso = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};
const today0 = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };

/* ── pack milestones into non-overlapping lanes ── */
function assignLanes(ms) {
  const sorted = [...ms]
    .filter(m => m.startDate && m.endDate)
    .sort((a, b) => toLocal(a.startDate) - toLocal(b.startDate));
  const lanes = [];
  const laneMap = {};
  for (const m of sorted) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const last = lanes[i][lanes[i].length - 1];
      if (toLocal(m.startDate) > toLocal(last.endDate)) {
        lanes[i].push(m);
        laneMap[m.id] = i;
        placed = true;
        break;
      }
    }
    if (!placed) {
      laneMap[m.id] = lanes.length;
      lanes.push([m]);
    }
  }
  return { laneMap, numLanes: Math.max(1, lanes.length) };
}

/* ── create / edit modal ── */
function MilestoneModal({ milestone, projects, onSave, onDelete, onClose }) {
  const isNew = !milestone?.id;
  const [form, setForm] = useState({
    name:        milestone?.name        || '',
    projectId:   milestone?.projectId   || (projects[0]?.id || ''),
    startDate:   milestone?.startDate   || toIso(today0()),
    endDate:     milestone?.endDate     || toIso(addDays(today0(), 13)),
    status:      milestone?.status      || 'planned',
    description: milestone?.description || '',
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
        style={{ background: 'var(--color-background)' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>
            {isNew ? 'New Milestone' : 'Edit Milestone'}
          </h2>
          <button onClick={onClose} style={{ color: 'var(--color-textMuted)' }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-textSecondary)' }}>Name</label>
            <input
              autoFocus
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Launch onboarding v2"
              onKeyDown={e => {
                if (e.key === 'Enter' && form.name.trim() && form.startDate && form.endDate) onSave(form);
              }}
            />
          </div>

          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-textSecondary)' }}>Project</label>
            <select
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
              value={form.projectId}
              onChange={e => set('projectId', e.target.value ? parseInt(e.target.value, 10) : '')}
            >
              <option value="">— No project —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-textSecondary)' }}>Start date</label>
              <input
                type="date"
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
                value={form.startDate}
                onChange={e => set('startDate', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-textSecondary)' }}>End date</label>
              <input
                type="date"
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
                value={form.endDate}
                onChange={e => set('endDate', e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-textSecondary)' }}>Status</label>
            <div className="flex gap-2">
              {Object.entries(STATUS).map(([key, s]) => (
                <button
                  key={key}
                  onClick={() => set('status', key)}
                  className="flex-1 py-1.5 rounded-lg text-xs font-medium border-2 transition-colors"
                  style={{
                    background:   form.status === key ? s.bg    : 'white',
                    borderColor:  form.status === key ? s.border : '#e2e8f0',
                    color:        form.status === key ? s.text   : '#94a3b8',
                  }}
                >{s.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-textSecondary)' }}>
              Description <span style={{ color: 'var(--color-textMuted)', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
              rows={2}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="What does this milestone represent?"
            />
          </div>
        </div>

        <div className="flex items-center justify-between mt-6">
          {!isNew ? (
            <button
              onClick={() => onDelete(milestone.id)}
              className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg"
              style={{ color: 'var(--color-textSecondary)', border: '1px solid var(--color-border)' }}
            >Cancel</button>
            <button
              onClick={() => onSave(form)}
              disabled={!form.name.trim() || !form.startDate || !form.endDate}
              className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors"
            >{isNew ? 'Create' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── PDF export modal ── */
function ExportModal({ groupedRows, onExport, onClose }) {
  const allMs = groupedRows.flatMap(g => g.rows.flatMap(r => r.milestones));
  const defaultStart = allMs.reduce((min, m) => m.startDate && m.startDate < min ? m.startDate : min, toIso(today0()));
  const defaultEnd   = allMs.reduce((max, m) => m.endDate   && m.endDate   > max ? m.endDate   : max, toIso(addDays(today0(), 90)));

  const [start,     setStart]     = useState(defaultStart);
  const [end,       setEnd]       = useState(defaultEnd);
  const [filterAll, setFilterAll] = useState(true);
  const [selected,  setSelected]  = useState(
    () => new Set(groupedRows.flatMap(g => g.rows.map(r => r.project.id)))
  );

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggleProject = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setFilterAll(false);
  };

  const selectAll = () => {
    setSelected(new Set(groupedRows.flatMap(g => g.rows.map(r => r.project.id))));
    setFilterAll(true);
  };

  const canExport = start && end && start <= end && (filterAll || selected.size > 0);

  const handleExport = () => {
    onExport({
      exportStart:        toLocal(start),
      exportEnd:          toLocal(end),
      selectedProjectIds: filterAll ? null : selected,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={onClose}
    >
      <div
        className="rounded-xl shadow-2xl p-6 w-full max-w-md mx-4"
        style={{ background: 'var(--color-background)', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5 flex-shrink-0">
          <h2 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
            <Download className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
            Export Timeline as PDF
          </h2>
          <button onClick={onClose} style={{ color: 'var(--color-textMuted)' }}><X className="w-4 h-4" /></button>
        </div>

        {/* Date range */}
        <div className="mb-4 flex-shrink-0">
          <label className="block text-xs font-medium mb-2" style={{ color: 'var(--color-textSecondary)' }}>
            Date range
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--color-textMuted)' }}>From</label>
              <input
                type="date"
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
                value={start}
                onChange={e => setStart(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs mb-1" style={{ color: 'var(--color-textMuted)' }}>To</label>
              <input
                type="date"
                className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }}
                value={end}
                onChange={e => setEnd(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs mt-1.5" style={{ color: 'var(--color-textMuted)' }}>
            {start && end && start <= end && (() => {
              const days = Math.round((toLocal(end) - toLocal(start)) / 86400000) + 1;
              return days <= 90
                ? `${days} days — day-column view`
                : `${days} days (${Math.ceil(days/7)} weeks) — week-column view`;
            })()}
          </p>
        </div>

        {/* Project filter */}
        <div className="mb-5 flex-1 min-h-0 flex flex-col">
          <div className="flex items-center justify-between mb-2 flex-shrink-0">
            <label className="text-xs font-medium" style={{ color: 'var(--color-textSecondary)' }}>Projects</label>
            <button
              onClick={selectAll}
              className="text-xs font-medium transition-colors"
              style={{ color: filterAll ? 'var(--color-primary)' : 'var(--color-textMuted)' }}
            >
              All projects
            </button>
          </div>
          <div className="overflow-auto flex-1" style={{ maxHeight: 200 }}>
            {groupedRows.map(({ goal, rows }) => (
              <div key={goal?.id ?? '__orphan__'} className="mb-3">
                {goal && (
                  <p className="text-xs font-bold uppercase mb-1.5"
                    style={{ color: 'var(--color-textMuted)', letterSpacing: '0.06em' }}>
                    {goal.name}
                  </p>
                )}
                <div className="space-y-1.5 pl-2">
                  {rows.map(({ project, milestones: pMs }) => (
                    <label key={project.id} className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filterAll || selected.has(project.id)}
                        onChange={() => toggleProject(project.id)}
                        className="rounded"
                        style={{ accentColor: 'var(--color-primary)' }}
                      />
                      <span className="text-xs" style={{ color: 'var(--color-text)' }}>{project.name}</span>
                      <span className="text-xs ml-auto" style={{ color: 'var(--color-textMuted)' }}>
                        {pMs.length} ms
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between flex-shrink-0 pt-2" style={{ borderTop: '1px solid var(--color-borderLight)' }}>
          <p className="text-xs" style={{ color: 'var(--color-textMuted)' }}>
            Landscape A4 · light theme · always
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg"
              style={{ color: 'var(--color-textSecondary)', border: '1px solid var(--color-border)' }}
            >Cancel</button>
            <button
              onClick={handleExport}
              disabled={!canExport}
              className="px-4 py-2 text-sm rounded-lg disabled:opacity-40 flex items-center gap-1.5 transition-colors"
              style={{ background: 'var(--color-text)', color: 'var(--color-background)' }}
            >
              <Download className="w-3.5 h-3.5" />
              Export PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── main timeline component ── */
export default function MilestoneTimeline({ milestones, projects, goals = [], onUpdateMilestone, onCreateMilestone, onDeleteMilestone }) {
  const [viewMode, setViewMode] = useState('week');
  const [editingMs, setEditingMs] = useState(null); // null | milestone | { _new: true, projectId? }
  const [dragOverride, setDragOverride_] = useState(null);
  const [collapsedGoals, setCollapsedGoals] = useState(new Set());
  const [showExportModal, setShowExportModal] = useState(false);

  const dragOverrideRef = useRef(null);
  const milestonesRef   = useRef(milestones);
  const dragRef         = useRef(null);

  useEffect(() => { milestonesRef.current = milestones; }, [milestones]);

  const setDragOverride = useCallback((v) => {
    dragOverrideRef.current = v;
    setDragOverride_(v);
  }, []);

  const dayW = viewMode === 'week' ? 38 : 16;

  /* ── view date range ── */
  const { viewStart, totalDays } = useMemo(() => {
    const now = today0();
    let earliest = now;
    let latest   = addDays(now, viewMode === 'week' ? 70 : 180);

    milestones.forEach(m => {
      if (m.startDate) { const s = toLocal(m.startDate); if (s < earliest) earliest = s; }
      if (m.endDate)   { const e = toLocal(m.endDate);   if (e > latest)   latest   = e; }
    });

    let raw = addDays(earliest, -14);
    const dow = raw.getDay();
    const viewStart = addDays(raw, dow === 1 ? 0 : dow === 0 ? 1 : 1 - dow);
    const end = addDays(latest, 21);
    const totalDays = Math.max(30, daysBetween(viewStart, end) + 1);
    return { viewStart, totalDays };
  }, [milestones, viewMode]);

  const todayX = useMemo(() => daysBetween(viewStart, today0()) * dayW, [viewStart, dayW]);

  const getX = (iso) => iso ? Math.max(0, daysBetween(viewStart, toLocal(iso)) * dayW) : 0;
  const getW = (s, e) => !s || !e ? dayW * 2 : Math.max(dayW * 2, (daysBetween(toLocal(s), toLocal(e)) + 1) * dayW);

  const getDisplay = (m) => {
    const ov = dragOverride;
    return ov && ov.id === m.id
      ? { startDate: ov.startDate, endDate: ov.endDate }
      : { startDate: m.startDate,  endDate: m.endDate  };
  };

  /* ── month spans for header ── */
  const monthSpans = useMemo(() => {
    const spans = [];
    let cur = null;
    for (let i = 0; i < totalDays; i++) {
      const d   = addDays(viewStart, i);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (!cur || cur.key !== key) {
        if (cur) spans.push(cur);
        cur = { key, label: d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), startI: i, count: 1 };
      } else { cur.count++; }
    }
    if (cur) spans.push(cur);
    return spans;
  }, [viewStart, totalDays]);

  /* ── day cells ── */
  const dayCells = useMemo(() => {
    const letters = ['S','M','T','W','T','F','S'];
    const now = today0();
    return Array.from({ length: totalDays }, (_, i) => {
      const d   = addDays(viewStart, i);
      const dow = d.getDay();
      return {
        i,
        isWeekend: dow === 0 || dow === 6,
        isToday:   daysBetween(viewStart, now) === i,
        label: viewMode === 'week' ? letters[dow] : (d.getDate() === 1 ? d.toLocaleDateString('en-US', { month: 'short' }) : ''),
      };
    });
  }, [viewStart, totalDays, viewMode]);

  /* ── drag to move / resize ── */
  const startDrag = useCallback((e, m, mode) => {
    e.preventDefault();
    e.stopPropagation();
    if (!m.startDate || !m.endDate) return;
    const origS = toLocal(m.startDate);
    const origE = toLocal(m.endDate);
    const dur   = daysBetween(origS, origE);
    dragRef.current = { id: m.id, startX: e.clientX, origS, origE, dur, mode };

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const deltaDays = Math.round((ev.clientX - dragRef.current.startX) / dayW);
      let s, e2;
      if (mode === 'move') {
        s  = addDays(dragRef.current.origS, deltaDays);
        e2 = addDays(s, dur);
      } else {
        s  = dragRef.current.origS;
        e2 = addDays(dragRef.current.origE, deltaDays);
        if (daysBetween(s, e2) < 0) e2 = new Date(s);
      }
      setDragOverride({ id: dragRef.current.id, startDate: toIso(s), endDate: toIso(e2) });
    };

    const onUp = () => {
      const ov   = dragOverrideRef.current;
      const full = milestonesRef.current.find(ms => ms.id === ov?.id);
      if (ov && full) onUpdateMilestone({ ...full, startDate: ov.startDate, endDate: ov.endDate });
      setDragOverride(null);
      dragRef.current = null;
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };

    document.body.style.cursor     = mode === 'move' ? 'grabbing' : 'ew-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  }, [dayW, onUpdateMilestone, setDragOverride]);

  const toggleGoal = useCallback((id) => {
    setCollapsedGoals(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  /* ── project rows grouped by goal ── */
  const buildProjectRow = useCallback((p) => {
    const pMs = milestones.filter(m => m.projectId === p.id);
    const { laneMap, numLanes } = assignLanes(pMs);
    const rowH = ROW_PAD + numLanes * LANE_H + ROW_PAD;
    return { project: p, milestones: pMs, laneMap, numLanes, rowH };
  }, [milestones]);

  const groupedRows = useMemo(() => {
    const active = projects.filter(p => p.active !== false);
    const groups = [];
    const seen = new Set();

    goals.filter(i => i.active !== false).forEach(init => {
      const iProjects = active.filter(p => p.goalId === init.id);
      if (iProjects.length === 0) return;
      groups.push({ goal: init, rows: iProjects.map(buildProjectRow) });
      iProjects.forEach(p => seen.add(p.id));
    });

    const orphans = active.filter(p => !seen.has(p.id));
    if (orphans.length > 0) {
      groups.push({ goal: null, rows: orphans.map(buildProjectRow) });
    }
    return groups;
  }, [milestones, projects, goals, buildProjectRow]);

  const allRows = useMemo(() => groupedRows.flatMap(g => g.rows), [groupedRows]);

  const totalW = LEFT_COL_W + totalDays * dayW;

  return (
    <div className="flex flex-col h-full" style={{ minHeight: 0 }}>

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between px-4 py-2.5 flex-shrink-0" style={{ borderBottom: '1px solid var(--color-border)', background: 'var(--color-background)' }}>
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--color-text)' }}>
            <Flag className="w-4 h-4 text-blue-500" />
            Milestones
          </h2>
          <div className="flex gap-0.5 rounded-lg p-0.5" style={{ background: 'var(--color-surface)' }}>
            {[['week','5-day week'],['month','Monthly']].map(([v,label]) => (
              <button
                key={v}
                onClick={() => setViewMode(v)}
                className="px-3 py-1 text-xs font-medium rounded-md transition-colors"
                style={viewMode === v
                  ? { background: 'var(--color-background)', color: 'var(--color-text)', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }
                  : { color: 'var(--color-textMuted)' }}
              >{label}</button>
            ))}
          </div>
          <span className="text-xs" style={{ color: 'var(--color-textMuted)' }}>Drag bars to move · drag edge to resize</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowExportModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            style={{ border: '1px solid var(--color-border)', color: 'var(--color-textSecondary)', background: 'var(--color-background)' }}
            title="Export timeline as PDF"
          >
            <Download className="w-3.5 h-3.5" /> Export PDF
          </button>
          <button
            onClick={() => setEditingMs({ _new: true })}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
            style={{ background: 'var(--color-text)', color: 'var(--color-background)' }}
          >
            <Plus className="w-3.5 h-3.5" /> Add milestone
          </button>
        </div>
      </div>

      {/* Empty-canvas hint — the grid alone gives no clue where to start */}
      {milestones.length === 0 && (
        <div style={{
          padding: '9px 16px', textAlign: 'center', fontSize: 12,
          color: 'var(--color-textMuted)', borderBottom: '1px solid var(--color-borderLight)',
          background: 'var(--color-surface)',
        }}>
          No milestones yet — hover a project on the left and click its <strong>+</strong>, or use <strong>Add milestone</strong> above, to put your first date on the map.
        </div>
      )}

      {/* ── Scrollable timeline ── */}
      <div className="flex-1 overflow-auto" style={{ minHeight: 0, background: 'var(--color-background)' }}>
        <div style={{ width: totalW, minWidth: '100%', position: 'relative', background: 'var(--color-background)' }}>

          {/* ─ Sticky header ─ */}
          <div style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--color-background)', borderBottom: '1px solid var(--color-border)' }}>

            {/* Month row */}
            <div style={{ display: 'flex', height: MONTH_HDR_H }}>
              <div style={{
                width: LEFT_COL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 25,
                background: 'var(--color-background)', borderRight: '1px solid var(--color-border)',
              }} />
              <div style={{ position: 'relative', flex: 1, height: MONTH_HDR_H }}>
                {monthSpans.map(span => (
                  <div
                    key={span.key}
                    style={{
                      position: 'absolute', left: span.startI * dayW, width: span.count * dayW, height: MONTH_HDR_H,
                      display: 'flex', alignItems: 'center', paddingLeft: 8,
                      borderLeft: '1px solid var(--color-border)',
                      fontSize: 10, fontWeight: 600, color: 'var(--color-textMuted)',
                      letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}
                  >{span.label}</div>
                ))}
              </div>
            </div>

            {/* Day row */}
            <div style={{ display: 'flex', height: DAY_HDR_H }}>
              <div style={{
                width: LEFT_COL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 25,
                background: 'var(--color-background)', borderRight: '1px solid var(--color-border)',
              }} />
              <div style={{ display: 'flex', flex: 1 }}>
                {dayCells.map(({ i, label, isWeekend, isToday }) => (
                  <div
                    key={i}
                    style={{
                      width: dayW, flexShrink: 0, height: DAY_HDR_H,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 9, fontWeight: isToday ? 700 : 400,
                      color:      isToday ? 'var(--color-primary)' : isWeekend ? 'var(--color-borderLight)' : 'var(--color-textMuted)',
                      background: isToday ? 'var(--color-phoLight)' : 'transparent',
                      borderLeft: '1px solid var(--color-borderLight)',
                    }}
                  >{label}</div>
                ))}
              </div>
            </div>
          </div>

          {/* ─ Today vertical line ─ */}
          {todayX >= 0 && (
            <div style={{
              position: 'absolute',
              left: LEFT_COL_W + todayX + Math.floor(dayW / 2),
              top: 0, bottom: 0, width: 2,
              background: '#ef4444', opacity: 0.5,
              zIndex: 4, pointerEvents: 'none',
            }} />
          )}

          {/* ─ Grouped project rows ─ */}
          {groupedRows.map(({ goal, rows: groupRows }) => {
            const isCollapsed = goal && collapsedGoals.has(goal.id);
            return (
              <React.Fragment key={goal?.id ?? '__orphan__'}>

                {/* Goal header row */}
                {goal && (
                  <div
                    onClick={() => toggleGoal(goal.id)}
                    style={{
                      display: 'flex', height: 30, cursor: 'pointer',
                      borderBottom: '1px solid var(--color-border)',
                      background: 'var(--color-surface)',
                    }}
                  >
                    <div style={{
                      width: LEFT_COL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10,
                      background: 'var(--color-surface)', borderRight: '1px solid var(--color-border)',
                      display: 'flex', alignItems: 'center', gap: 4,
                      paddingLeft: 10, paddingRight: 8,
                    }}>
                      <ChevronRight style={{
                        width: 13, height: 13, flexShrink: 0,
                        color: 'var(--color-textMuted)',
                        transition: 'transform 0.15s',
                        transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
                      }} />
                      <span style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: 'var(--color-textMuted)',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{goal.name}</span>
                    </div>
                    <div style={{ flex: 1, background: 'var(--color-surface)' }}>
                      {/* Weekend shading in header */}
                      <div style={{ position: 'relative', height: '100%' }}>
                        {dayCells.filter(c => c.isWeekend).map(c => (
                          <div key={c.i} style={{
                            position: 'absolute', left: c.i * dayW, top: 0, bottom: 0,
                            width: dayW, background: 'rgba(0,0,0,0.03)', pointerEvents: 'none',
                          }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Project rows (hidden when collapsed) */}
                {!isCollapsed && groupRows.map(({ project, milestones: pMs, laneMap, rowH }) => (
                  <div
                    key={project.id}
                    style={{ display: 'flex', borderBottom: '1px solid var(--color-borderLight)', minHeight: rowH, position: 'relative' }}
                  >
                    {/* Sticky project label */}
                    <div style={{
                      width: LEFT_COL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10,
                      background: 'var(--color-background)', borderRight: '1px solid var(--color-border)',
                      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
                      padding: '10px 10px 10px 14px',
                    }}>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.name}
                        </p>
                        {pMs.length > 0 && (
                          <p style={{ fontSize: 10, color: 'var(--color-textMuted)', marginTop: 2 }}>
                            {pMs.length} milestone{pMs.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingMs({ _new: true, projectId: project.id }); }}
                        title="Add milestone to this project"
                        style={{ color: 'var(--color-border)', flexShrink: 0, paddingTop: 2 }}
                        onMouseEnter={e => e.currentTarget.style.color = 'var(--color-textMuted)'}
                        onMouseLeave={e => e.currentTarget.style.color = 'var(--color-border)'}
                      >
                        <Plus style={{ width: 14, height: 14 }} />
                      </button>
                    </div>

                    {/* Timeline area */}
                    <div style={{ flex: 1, position: 'relative', minHeight: rowH, background: 'var(--color-background)' }}>
                      {dayCells.filter(c => c.isWeekend).map(c => (
                        <div key={c.i} style={{
                          position: 'absolute', left: c.i * dayW, top: 0, bottom: 0,
                          width: dayW, background: 'var(--color-surface)', pointerEvents: 'none',
                        }} />
                      ))}
                      {dayCells.filter(c => c.i % 7 === 0).map(c => (
                        <div key={c.i} style={{
                          position: 'absolute', left: c.i * dayW, top: 0, bottom: 0,
                          width: 1, background: 'var(--color-borderLight)', pointerEvents: 'none',
                        }} />
                      ))}
                      {pMs.map(m => {
                        const { startDate, endDate } = getDisplay(m);
                        const x    = getX(startDate);
                        const w    = getW(startDate, endDate);
                        const lane = laneMap[m.id] ?? 0;
                        const y    = ROW_PAD + lane * LANE_H + (LANE_H - BAR_H) / 2;
                        const s    = STATUS[m.status] || STATUS.planned;
                        const label = viewMode === 'week'
                          ? m.name
                          : `${m.name} (${startDate?.slice(5) ?? ''} – ${endDate?.slice(5) ?? ''})`;
                        return (
                          <div
                            key={m.id}
                            title={`${m.name}\n${startDate} → ${endDate}`}
                            style={{
                              position: 'absolute', left: x, top: y, width: w, height: BAR_H,
                              background: s.bg, border: `2px solid ${s.border}`, borderRadius: 6,
                              cursor: 'grab', userSelect: 'none',
                              display: 'flex', alignItems: 'center', overflow: 'hidden',
                              zIndex: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                            }}
                            onMouseDown={(e) => startDrag(e, m, 'move')}
                            onClick={(e) => { e.stopPropagation(); setEditingMs(m); }}
                          >
                            <span style={{
                              flex: 1, fontSize: 11, fontWeight: 600, color: s.text,
                              paddingLeft: 8, paddingRight: 4,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>{label}</span>
                            <div
                              style={{ width: 12, flexShrink: 0, cursor: 'ew-resize', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              onMouseDown={(e) => startDrag(e, m, 'resize')}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div style={{ width: 3, height: 12, borderRadius: 2, background: s.border, opacity: 0.7 }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </React.Fragment>
            );
          })}

          {/* Empty state */}
          {allRows.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', color: 'var(--color-textMuted)' }}>
              <Flag style={{ width: 40, height: 40, marginBottom: 12, opacity: 0.25 }} />
              <p style={{ fontSize: 14, fontWeight: 500 }}>No milestones yet</p>
              <p style={{ fontSize: 12, marginTop: 4 }}>Create a project first, then add milestones to start your roadmap.</p>
            </div>
          )}

          {/* Unlinked milestones */}
          {(() => {
            const unlinked = milestones.filter(m => !m.projectId || !projects.find(p => p.id === m.projectId));
            if (!unlinked.length) return null;
            const { laneMap, numLanes } = assignLanes(unlinked);
            const rowH = ROW_PAD + numLanes * LANE_H + ROW_PAD;
            return (
              <div style={{ display: 'flex', borderBottom: '1px solid var(--color-borderLight)', minHeight: rowH, position: 'relative' }}>
                <div style={{
                  width: LEFT_COL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 10,
                  background: 'var(--color-surface)', borderRight: '1px solid var(--color-border)',
                  display: 'flex', alignItems: 'flex-start', padding: '10px 10px 10px 14px',
                }}>
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-textMuted)', fontStyle: 'italic' }}>Unlinked</p>
                    <p style={{ fontSize: 10, color: 'var(--color-textMuted)', marginTop: 2 }}>{unlinked.length} milestone{unlinked.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div style={{ flex: 1, position: 'relative', minHeight: rowH }}>
                  {unlinked.map(m => {
                    const { startDate, endDate } = getDisplay(m);
                    const x = getX(startDate); const w = getW(startDate, endDate);
                    const lane = laneMap[m.id] ?? 0;
                    const y = ROW_PAD + lane * LANE_H + (LANE_H - BAR_H) / 2;
                    const s = STATUS[m.status] || STATUS.planned;
                    return (
                      <div
                        key={m.id}
                        title={m.name}
                        style={{ position: 'absolute', left: x, top: y, width: w, height: BAR_H, background: s.bg, border: `2px solid ${s.border}`, borderRadius: 6, cursor: 'grab', userSelect: 'none', display: 'flex', alignItems: 'center', overflow: 'hidden', zIndex: 2 }}
                        onMouseDown={(e) => startDrag(e, m, 'move')}
                        onClick={(e) => { e.stopPropagation(); setEditingMs(m); }}
                      >
                        <span style={{ flex: 1, fontSize: 11, fontWeight: 600, color: s.text, paddingLeft: 8, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                        <div style={{ width: 12, flexShrink: 0, cursor: 'ew-resize', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseDown={(e) => startDrag(e, m, 'resize')} onClick={e => e.stopPropagation()}>
                          <div style={{ width: 3, height: 12, borderRadius: 2, background: s.border, opacity: 0.7 }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ── Export modal ── */}
      {showExportModal && (
        <ExportModal
          groupedRows={groupedRows}
          onExport={(opts) => exportTimelinePDF({ groupedRows, ...opts })}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* ── Edit / Create modal ── */}
      {editingMs && (
        <MilestoneModal
          milestone={editingMs._new ? null : editingMs}
          projects={projects.filter(p => p.active !== false)}
          onSave={(form) => {
            const data = {
              ...form,
              projectId: form.projectId ? parseInt(form.projectId, 10) : null,
            };
            if (editingMs._new) {
              onCreateMilestone({ ...data, projectId: data.projectId || (editingMs.projectId || null) });
            } else {
              onUpdateMilestone({ ...editingMs, ...data });
            }
            setEditingMs(null);
          }}
          onDelete={(id) => { onDeleteMilestone(id); setEditingMs(null); }}
          onClose={() => setEditingMs(null)}
        />
      )}
    </div>
  );
}
